import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import type { SignupProvider } from '@/features/Integrations/connectToolsState';
import { getFirebaseAuth } from '@/features/Auth/gcp/firebase';
import { providerFromIds } from '@/features/Integrations/connectToolsState';

/**
 * Which provider this person signed in with, as Identity Platform knows it.
 *
 * READ FROM THE FIREBASE USER, NOT FROM A SIGN-UP FLAG THE APP REMEMBERS. The
 * three ways in (email and password, Google, and Microsoft once its sign-up
 * ships) all end as an Identity Platform user whose `providerData` names how
 * they authenticate, so one read covers every route and cannot drift from them.
 *
 * `null` means "cannot tell" and is answered honestly, never guessed: an email
 * and password account has no mailbox provider of its own, and the step asks.
 * Firebase restores its user asynchronously, so the first value can be `null`
 * before the real one arrives; the subscription settles it. Where Firebase is
 * not configured at all (a build without it) the hook stays `null` rather than
 * throwing.
 */
export function useSignInProvider(): SignupProvider | null {
  const [provider, setProvider] = useState<SignupProvider | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    try {
      unsubscribe = onAuthStateChanged(getFirebaseAuth(), (user) => {
        setProvider(
          providerFromIds((user?.providerData ?? []).map((p) => p.providerId)),
        );
      });
    } catch {
      setProvider(null);
    }
    return () => unsubscribe?.();
  }, []);

  return provider;
}
